import { useEffect, useState } from 'react';

import { akamaiLoader } from '@/utils/akamaiLoader';
import type {
  MediaWrapperContainerTypeEnum,
  MediaWrapperTypeEnum,
} from '@/utils/aspectRatioUtils';
import { getAspectRatioForImages } from '@/utils/aspectRatioUtils';
import { getLinkAriaLabel } from '@/utils/urlUtils';

import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { Video } from '@/components/Video';
import type {
  ContentfulAsset,
  ContentfulImageWithFocalPointFragment,
} from '@/graphql/contentful/api';
import { useIsMdDown } from '@/hooks/useMedia';
import { breakpoints } from '@/theme/breakpoints';

import { DesktopImage, MobileImage } from './components';

import * as S from './MediaWrapper.styles';

export interface MediaWrapperProps
  extends ContentfulImageWithFocalPointFragment {
  aspectRatio?: { x: number; y: number };
  cardGroupType?: string;
  className?: string;
  loaderProps?: { height: number };
  mediaWrapperType?: string;
  position?: string;
  preload?: boolean;
  setLoadingState?: (state: string) => void;
  sizes?: string;
  useAspectRatio?: boolean;
  useLoader?: boolean;
  useVideo?: boolean;
  width?: string;
  widths?: number[];
}

export const MediaWrapper = (props: MediaWrapperProps) => {
  const {
    alt,
    cardGroupType,
    className,
    focalPoint,
    image,
    imageMobile,
    imageOnHover,
    imageOnHoverMobile,
    internalName,
    loaderProps,
    mediaWrapperType,
    preload,
    setLoadingState,
    sizes,
    url,
    useLoader,
    useVideo = true,
    video,
    videoMobile,
    videoPlayOption,
    width,
  } = props;
  const isMobile = useIsMdDown();
  const [poster, setPoster] = useState<string>('');
  const [videoUrl, setVideoUrl] = useState<string>('');

  const mobileImageFocalPoint = {
    focalPoint: {
      x: ((imageMobile?.width ?? 0) as number) / 2,
      y: ((imageMobile?.height ?? 0) as number) / 2,
    },
  };

  const _imageDesktop = [] as ContentfulAsset[];
  const _imageMobile = [] as ContentfulAsset[];

  !!image && _imageDesktop.push(image as ContentfulAsset);
  !!imageOnHover && _imageDesktop.push(imageOnHover as ContentfulAsset);
  !!(imageMobile ?? image) &&
    _imageMobile.push((imageMobile ?? image) as ContentfulAsset);
  !!(imageOnHoverMobile ?? imageOnHover) &&
    _imageMobile.push((imageOnHoverMobile ?? imageOnHover) as ContentfulAsset);
  const _video = useVideo
    ? isMobile && videoMobile
      ? videoMobile
      : video
    : null;
  const getAspectRatioData = (deviceType?: string) => {
    const image = deviceType === 'mobile' ? _imageMobile[0] : _imageDesktop[0];
    return (
      getAspectRatioForImages(
        mediaWrapperType as MediaWrapperTypeEnum,
        cardGroupType as MediaWrapperContainerTypeEnum,
        image?.width,
        image?.height,
        deviceType,
      ) ?? { x: 1, y: 1 }
    );
  };

  const getAkamaiImage = (deviceType?: string) => {
    const image = deviceType === 'mobile' ? _imageMobile[0] : _imageDesktop[0];
    const akamaiFocalPoint =
      deviceType === 'mobile' ? mobileImageFocalPoint : focalPoint;
    const aspectRatio = getAspectRatioData(deviceType);

    return akamaiLoader({
      focalPoint: akamaiFocalPoint?.focalPoint,
      aspectRatioWidth: aspectRatio.x,
      aspectRatioHeight: aspectRatio.y,
      width: image?.width as number,
      src: image?.url as string,
    });
  };

  const imageProps = {
    alt: alt || internalName,
    className,
    loaderProps,
    preload,
    setLoadingState,
    sizes,
    useLoader,
    width,
    mediaWrapperType,
  };

  useEffect(() => {
    const updatePoster = () => {
      if (_video) {
        if (window.matchMedia(`(min-width: ${breakpoints.md})`).matches) {
          setPoster(getAkamaiImage('desktop'));
        } else {
          setPoster(getAkamaiImage('mobile'));
        }
        setVideoUrl(
          akamaiLoader({
            src: _video.url ?? '',
          }),
        );
      }
    };
    updatePoster();
    window.addEventListener('resize', updatePoster);

    return () => {
      window.removeEventListener('resize', updatePoster);
    };
  }, []);

  return (
    <S.Box
      aria-label={getLinkAriaLabel(url ?? '')}
      as={url ? LinkWithLocale : 'div'}
      {...(!!url && { href: url })}
      $MediaWrapperType={mediaWrapperType}
      data-test-id='MediaWrapperBox'
    >
      {_imageDesktop.length > 0 && _imageMobile.length > 0 && !_video && (
        <S.ImageWrapper $mediaWrapperType={mediaWrapperType}>
          <MobileImage
            {...imageProps}
            _focalPoint={imageMobile ? mobileImageFocalPoint : focalPoint}
            _image={_imageMobile}
            aspectRatioData={getAspectRatioData('mobile')}
          />
          <DesktopImage
            {...imageProps}
            _focalPoint={focalPoint}
            _image={_imageDesktop}
            aspectRatioData={getAspectRatioData('desktop')}
          />
        </S.ImageWrapper>
      )}
      {_video && (
        <S.VideoWrapper $videoPlayOption={videoPlayOption as string}>
          <Video
            controls={false}
            data-test-id='MediaWrapperVideo'
            desktopAspectRatio={getAspectRatioData('desktop')}
            mediaUrl={videoUrl}
            mobileAspectRatio={getAspectRatioData('mobile')}
            poster={poster}
            setLoadingState={setLoadingState}
            title={_video.title}
            videoPlayOption={videoPlayOption}
          />
        </S.VideoWrapper>
      )}
    </S.Box>
  );
};

MediaWrapper.displayName = 'MediaWrapper';
